import { useEffect, useState } from 'preact/hooks'
import { RefreshCw } from 'lucide-preact'
import { useWorkspaceEmailActivity } from '@/hooks/useWorkspaceEmailActivity'
import { EmailActivityFilters } from './EmailActivityFilters'
import { EmailActivityArmedBand } from './EmailActivityArmedBand'
import { EmailActivityTable } from './EmailActivityTable'
import { EmailActivityDetailFlyout } from './EmailActivityDetailFlyout'
import type { EmailActivityRowDto, EmailActivityStatusFilter } from '@/types/emailActivity'

interface Props {
    clientCompanyId: string
    workspaceId: string
}

// Workspace email-activity view (ORB-385): the operational queue/triage of every email to a person across
// the workspace. Lands on the Upcoming queue (armed-broadcasts band + scheduled sends) and filters outward
// to sent / delivered / opened / clicked / bounced / failed. This page owns the filter/search/page state
// and orchestrates the filter bar, armed band, table, and detail flyout.
export function WorkspaceEmailActivityPage({ clientCompanyId, workspaceId }: Props) {
    const [status, setStatus] = useState<EmailActivityStatusFilter>('upcoming')
    const [type, setType] = useState<string | undefined>(undefined)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [selectedRow, setSelectedRow] = useState<EmailActivityRowDto | null>(null)

    // Debounce the recipient search.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput), 300)
        return () => clearTimeout(timer)
    }, [searchInput])

    // Any filter / search change resets to the first page.
    useEffect(() => { setPage(0) }, [status, type, search])

    const { data, loading, error, pageSize, reload } = useWorkspaceEmailActivity({ workspaceId, status, type, search, page })

    const items = data?.Items ?? []
    const armed = status === 'upcoming' ? (data?.ArmedBroadcasts ?? []) : []
    const total = data?.Total ?? 0

    return (
        <div class="px-4 py-6 sm:px-6">
            <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div class="flex flex-wrap items-end justify-between gap-3 px-6 pb-4 pt-5">
                    <div>
                        <h1 class="text-xl font-semibold text-slate-900 dark:text-white">Emails</h1>
                        <p class="mt-0.5 text-sm text-slate-500 dark:text-slate-400">Everything queued, sending, and sent to your people across this workspace.</p>
                    </div>
                    <button type="button" onClick={reload} class="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">
                        <RefreshCw size={14} />Refresh
                    </button>
                </div>

                <EmailActivityFilters
                    status={status}
                    onStatusChange={setStatus}
                    type={type}
                    onTypeChange={setType}
                    searchInput={searchInput}
                    onSearchInput={setSearchInput}
                />

                <EmailActivityArmedBand armed={armed} clientCompanyId={clientCompanyId} workspaceId={workspaceId} />

                {loading && !data ? (
                    <div class="px-6 py-16 text-center text-sm text-slate-500 dark:text-slate-400">Loading…</div>
                ) : error ? (
                    <div class="px-6 py-16 text-center">
                        <p class="text-sm text-red-600 dark:text-red-400">{error}</p>
                        <button type="button" onClick={reload} class="mt-3 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">Retry</button>
                    </div>
                ) : items.length === 0 && armed.length === 0 ? (
                    <EmptyState status={status} search={search} />
                ) : (
                    <EmailActivityTable
                        items={items}
                        clientCompanyId={clientCompanyId}
                        workspaceId={workspaceId}
                        onRowClick={setSelectedRow}
                        page={page}
                        total={total}
                        pageSize={pageSize}
                        onPageChange={setPage}
                    />
                )}
            </section>

            {selectedRow && (
                <EmailActivityDetailFlyout row={selectedRow} clientCompanyId={clientCompanyId} workspaceId={workspaceId} onClose={() => setSelectedRow(null)} />
            )}
        </div>
    )
}

function EmptyState({ status, search }: { status: string; search: string }) {
    const message = search
        ? `No recipients match "${search}".`
        : status === 'upcoming'
            ? 'Scheduled sends and armed broadcasts appear here before they fire. Filter to Sent to see what has already gone out.'
            : `No ${status} emails in this range.`
    return (
        <div class="flex flex-col items-center justify-center px-6 py-20 text-center">
            <div class="grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>
            </div>
            <p class="mt-4 text-sm font-medium text-slate-700 dark:text-slate-200">{status === 'upcoming' ? 'Nothing queued' : 'No emails'}</p>
            <p class="mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">{message}</p>
        </div>
    )
}
